import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import * as _ from 'lodash';
import * as moment from 'moment';
import { LocaleService } from 'src/app/services/locale.service';
import { RegistrationService } from 'src/app/services/registration.service';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import 'moment/locale/es';
import { AppPaths } from 'src/app/enums/app-paths.enum';
import { Language } from 'src/app/models/language.model';
import { ScriptService } from 'src/app/services/script-service';
import { Patient } from 'src/app/models/patient.model';
import { CustomRouter } from 'src/app/routers/custom-router';
import { PatientService } from 'src/app/services/patient.service';
import { CountryISO, TextLabels } from 'ngx-material-intl-tel-input';
import { convertDateToTimestamp, convertTimestampToDate } from '../../../../util/functions/date-util.function';
import { Provider } from 'src/app/models/provider.model';
import { Country } from 'src/app/models/country.model';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subscription } from 'rxjs';
import { getValidPhoneNumber } from 'src/app/util/functions/phone.function';
import { fullNameValidator } from 'src/app/functions/validators/fullname.validator';
import { DOCUMENT_TYPES_BRAZIL } from 'src/app/consts/document-types';
import { ActivatedRoute } from '@angular/router';
import { PATTERN } from 'src/app/consts/pattern.const';

@Component({
  selector: 'app-data-validation',
  templateUrl: './data-validation.component.html',
  styleUrls: ['./data-validation.component.css'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'es-ar' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: false } }
  ],
  standalone: false
})
export class DataValidationComponent implements OnInit, OnDestroy {
  genders = [
    { name: 'gender_female', value: 'F' },
    { name: 'gender_male', value: 'M' },
    { name: 'gender_undefined', value: 'X' }
  ];

  @Output() updatedData = new EventEmitter();
  @Input() patient!: Patient;
  @Input() showScrollHint = false;

  dataFormGroup!: FormGroup;

  provider: Provider = this.registrationService.getProvider();

  providerLanguages: Language[] = [];
  selectedLanguage: any;
  requiredFields: string[] = [];

  whiteLabel: boolean;

  readonly preferredCountries: CountryISO[] = [CountryISO.Argentina, CountryISO.Brazil, CountryISO.UnitedStates];
  selectedCountry!: CountryISO;
  textLabelsPhone: TextLabels = {
    mainLabel: '',
    codePlaceholder: '',
    searchPlaceholderLabel: '',
    noEntriesFoundLabel: '',
    nationalNumberLabel: '',
    hintLabel: '',
    invalidNumberError: this.localeService.getString('account_phone_number_error'),
    requiredError: this.localeService.getString('field_error_required')
  };

  loadedFields = false;
  showCUILField = false;

  scrollHintConfig = {
    bottom: '',
    width: '',
    left: ''
  };
  breakpointSubscription?: Subscription;
  needsValidateName = sessionStorage.getItem('needsValidateName') === 'true';
  readonly maximumDate: Date = moment().endOf('day').toDate();

  isSelectedLanguageDifferentFromLocale = false;
  areDocumentTypeAndNumberRequiredFields = false;
  documentTypes: { text: string; value: string }[] = [];

  constructor(
    private route: ActivatedRoute,
    private registrationService: RegistrationService,
    private localeService: LocaleService,
    private customRouter: CustomRouter,
    private _adapter: DateAdapter<any>,
    private scriptService: ScriptService,
    private renderer: Renderer2,
    private patientService: PatientService,
    private fb: FormBuilder,
    private breakpointObserver: BreakpointObserver
  ) {
    this.whiteLabel = this.registrationService.whiteLabel;
    this.buildForm();

    this.breakpointSubscription = this.breakpointObserver.observe(['(max-width: 600px)']).subscribe((result: { matches: boolean }) => {
      const isMobile = result.matches;
      if (!isMobile) {
        this.scrollHintConfig = {
          bottom: 'calc(70px + 13%)', // 'actions' class + footer height
          width: '50%',
          left: 'unset'
        };
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.insertCookiesScript();
    this.patient = this.patient ?? this.registrationService.getPatient();
    this.provider = _.isObject(this.patient?.provider) ? this.patient?.provider : this.provider;
    if (!this.patient || _.isEmpty(this.patient)) {
      this.customRouter.navigate([`/${AppPaths.AUTH}`]);
      return;
    }

    const countryCode = (this.provider.country as Country)?.countryCode;
    this.areDocumentTypeAndNumberRequiredFields = countryCode === 'BR';
    if (countryCode === 'BR') this.documentTypes = DOCUMENT_TYPES_BRAZIL;

    const preselectedCaller = await this.patientService.getPreselectedCaller(this.patient);
    if (preselectedCaller && this.patient.id !== preselectedCaller.id) {
      if (!this.patientService.isAgeAtLeast(preselectedCaller, 15)) {
        preselectedCaller.email = this.patient.email;
        preselectedCaller.mobilePhone = this.patient.mobilePhone;
      }
      this.patient = preselectedCaller;
    }

    this.setSelectedLanguage();

    this.selectedCountry = this.localeService.getCountryISO();
    this.setDatePickerLocale();
    this.requiredFields = await this.patientService.getRequiredFields();
    await this.setFormValues();
  }

  private setSelectedLanguage(): void {
    this.providerLanguages = this.provider.supportedLanguages;
    if (!this.providerLanguages || !this.providerLanguages.length) return;

    const locales = [this.route.snapshot.queryParams.locale, this.provider.language?.split('-')[0], this.localeService.getLanguageISO()];
    let selectedLocale: string | undefined;
    for (const locale of locales) {
      if (!locale) continue;
      this.selectedLanguage = this.providerLanguages.find((language: Language) => language.code === locale);
      if (this.selectedLanguage) {
        selectedLocale = locale
        this.setLocaleIdInStorage();
        break;
      }
    }

    if (selectedLocale !== this.localeService.locale) this.isSelectedLanguageDifferentFromLocale = true
  }

  ngOnDestroy(): void {
    this.breakpointSubscription?.unsubscribe();
  }

  buildForm(): void {
    this.dataFormGroup = this.fb.group({
      birthDate: ['', [Validators.required]],
      mobilePhone: [undefined, [Validators.required]],
      gender: ['', [Validators.required]]
    });
  }

  async setFormValues(): Promise<void> {
    if (this.requiredFields.includes('email')) {
      let initialValue = '';
      if (!this.patient?.email) {
        const familyGroupOwner = await this.patientService.getFamilyGroupOwner();
        initialValue = familyGroupOwner?.email || '';
      }
      this.dataFormGroup.addControl(
        'email',
        this.fb.control(initialValue, [Validators.required, Validators.email, Validators.pattern(PATTERN.email)])
      );
    }

    if (this.requiredFields.includes('emailLAD')) {
      this.dataFormGroup.addControl(
        'emailLAD',
        this.fb.control(this.getEmailLAD(this.patient), [Validators.required, Validators.email, Validators.pattern(PATTERN.email)])
      );
    }

    if (this.requiredFields.includes('name'))
      this.dataFormGroup.addControl('name', this.fb.control(this.patient.name, [Validators.required, fullNameValidator]));

    if (this.requiredFields.includes('documentTypeLAD') || this.requiredFields.includes('documentNumberLAD')) {
      let documentNumber = null;
      let documentTypeValue = null;
      const documentTypeLAD = this.documentTypes.find((type) => type.value === this.patient.documentTypeLAD);
      if (documentTypeLAD) {
        documentTypeValue = documentTypeLAD;
        documentNumber = this.patient.documentNumberLAD;
      } else {
        documentTypeValue = this.documentTypes.find((type) => type.value === this.patient.documentType);
        documentNumber = this.patient.documentNumber;
      }

      this.dataFormGroup.addControl(
        'documentTypeLAD',
        this.fb.control(documentTypeValue || this.documentTypes.find((type) => type.value === 'PAS'), Validators.required)
      );
      this.dataFormGroup.addControl('documentNumberLAD', this.fb.control(documentNumber, Validators.required));
    }

    this.dataFormGroup.patchValue({
      birthDate: this.getBirthDate(this.patient),
      mobilePhone: this.getPhone(this.patient),
      gender: this.patient!.gender
    });

    this.loadedFields = true;
    this.dataFormGroup.markAllAsTouched();
  }

  continue() {
    const birthdateUnix = convertDateToTimestamp(this.dataFormGroup.value.birthDate);

    const formValues = this.dataFormGroup.value;
    if (formValues.name) formValues.name = formValues.name.trim();
    if (formValues.email) formValues.email = formValues.email.trim();
    if (formValues.emailLAD) formValues.emailLAD = formValues.emailLAD.trim();
    if (formValues.documentNumberLAD) formValues.documentNumberLAD = formValues.documentNumberLAD.trim();
    if (formValues.documentTypeLAD) formValues.documentTypeLAD = formValues.documentTypeLAD?.value || formValues.documentTypeLAD;

    this.updatedData.emit({ ...formValues, birthDate: birthdateUnix });
  }

  onLocaleChanged(): void {
    this.setLocaleIdInStorage();
  }

  private setLocaleIdInStorage(): void {
    this.localeService.setLocaleIdInStorage(this.selectedLanguage.id);
  }

  getBirthDate(patient: any): string | Date {
    const date = patient.birthDate ?? (patient as any).birthdate;
    if (!date && date !== 0) return '';
    if (typeof date === 'string' && (date.includes('-') || date.includes('/'))) {
      return moment(date, 'DD-MM-YYYY').toDate();
    }
    const dateObject = convertTimestampToDate(date);
    console.log('getBirthDate: convertTimestampToDate of ', date, 'date returned:', dateObject);
    console.log('getBirthDate: returning ', new Date(dateObject.getUTCFullYear(), dateObject.getUTCMonth(), dateObject.getUTCDate()));
    return new Date(dateObject.getUTCFullYear(), dateObject.getUTCMonth(), dateObject.getUTCDate());
  }

  getPhone(patient: Patient): string {
    return getValidPhoneNumber(patient.mobilePhone || patient.phone, this.selectedCountry);
  }

  getEmailLAD(patient: any) {
    return patient.emailLAD || patient.email || patient.secondaryEmail || '';
  }

  private setDatePickerLocale(): void {
    const locale = this.localeService.getLanguageISO();
    this._adapter.setLocale(locale);
  }

  insertCookiesScript(): void {
    this.scriptService.insertCookiesScript(this.renderer);
  }

  compareWithDocumentTypeFn = (option: string, value: { text: string; value: string }) => {
    return option === value?.value;
  };

  get mobilePhone(): FormControl<string> {
    return this.dataFormGroup.get('mobilePhone') as FormControl;
  }
  get cuil(): FormControl<string> {
    return this.dataFormGroup.get('cuil') as FormControl;
  }
}
